import { useEffect, useRef, useState } from "react";

/**
 * Types a string character by character with subtle humanizing jitter.
 * Skips animation under prefers-reduced-motion.
 *
 * Returns { value, isTyping, skip } so callers can render a caret and
 * disable downstream actions until typing completes.
 */
export function useTypewriter(
  target: string,
  options: {
    enabled?: boolean;
    /** Base ms per character. Default 28. */
    speed?: number;
    /** Random ±jitter (ms) added per character. Default 10. */
    jitter?: number;
    /** Reset key — when it changes, re-type from scratch. */
    resetKey?: string | number;
  } = {},
) {
  const { enabled = true, speed = 28, jitter = 10, resetKey } = options;
  const [value, setValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const stoppedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    stoppedRef.current = false;
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!enabled) {
      setValue("");
      setIsTyping(false);
      return;
    }

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion || !target) {
      setValue(target);
      setIsTyping(false);
      return;
    }

    setValue("");
    setIsTyping(true);
    let i = 0;

    const tick = () => {
      if (stoppedRef.current) return;
      i += 1;
      setValue(target.slice(0, i));
      if (i >= target.length) {
        setIsTyping(false);
        return;
      }
      const delta = speed + (Math.random() * 2 - 1) * jitter;
      timeoutRef.current = window.setTimeout(tick, Math.max(8, delta));
    };

    timeoutRef.current = window.setTimeout(tick, speed);

    return () => {
      stoppedRef.current = true;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, enabled, resetKey]);

  const skip = () => {
    stoppedRef.current = true;
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsTyping(false);
  };

  return { value, isTyping, skip, setValue };
}
